import { FAST_API_URL, postOptions } from "../index";
import { ApiError, errorReason, throwIfNotOk } from "./httpErrors";
import { cuesRuGen } from "../../utils/pluralRu";
import { CutAudioArgs, CutAudioResponse, DeleteAudioArgs, SyntAudioArgs, SyntAudioResponse } from "./types";

export const syntAudioApi = async ({
    mediaId,
    lang,
    metasubtitle_id,
    folder_num,
    voice,
    speed,
    language_from,
    synt_audio_type,
    adjust,
    unskip,
    voiceNamed,
}: SyntAudioArgs): Promise<SyntAudioResponse> => {
    // `adjust=false` уходит только в режиме «без автоправок»: без него запрос остаётся ровно
    // тем, что был, — и у лекций без флага сервер не видит никакой разницы.
    const response = await fetch(
        `${FAST_API_URL}/synt_audio?media=${mediaId}&lang=${lang}&metasubtitle_id=${metasubtitle_id}&folder_num=${folder_num}&voice=${voice}&speed=${speed}&language_from=${language_from || ''}&synt_audio_type=${synt_audio_type || 'tts'}${adjust === false ? '&adjust=false' : ''}${unskip ? '&unskip=true' : ''}${voiceNamed === undefined ? '' : `&voice_named=${voiceNamed ? 'true' : 'false'}`}`,
    );
    // Причина отказа — словами сервера: «нет перевода», «время реплики испорчено», «голос не
    // определён». Раньше здесь был один «cant synt audio», и переводчик не видел ничего.
    await throwIfNotOk(response, "cant synt audio");
    return await response.json();
};

export const deleteAudioApi = async ({ mediaId, lang, folder_num, id, language_from }: DeleteAudioArgs) => {
    const response = await fetch(`${FAST_API_URL}/delete_synt_audio?media=${mediaId}`, {
        ...postOptions,
        body: JSON.stringify({ id, folder_num, lang, language_from }),
    });
    // Отказ — словами сервера: им удаляется и озвучка без реплики оригинала (orphanVoicings.ts),
    // и переводчица должна прочесть в красной полосе, почему звук остался.
    await throwIfNotOk(response, "cant delete audio");
    return await response.json();
};

export const cutAudioWithoutPause = async ({
    mediaId,
    lang,
    folder_num,
    cut_snippets,
    language_from
}: CutAudioArgs): Promise<CutAudioResponse> => {
    const response = await fetch(`${FAST_API_URL}/cut_without_pauses?media=${mediaId}`, {
        ...postOptions,
        body: JSON.stringify({ cut_snippets, folder_num, lang, language_from }),
    });
    // Отказ доходит до окна «Cut video Error» (Footer/Voicing): три причины TP #129 — простыми
    // словами (cutPausesRefusal), прочие — словами сервера; «cant cut pauses» — только без тела.
    await throwIfNotOk(response, "cant cut pauses").catch((error: ApiError) => {
        error.message = cutPausesRefusal(error) ?? error.message;
        throw error;
    });
    return await response.json();
};

/**
 * Почему «Вырезать паузы» ничего не сделала — для переводчика (TP #129, 03.11.2026). Сервер
 * больше не падает 500, а отказывает до любой записи: у реплик нет озвучки (409
 * `cues_without_dub`: сколько и какие — время в папке и текст), у папки нет исходного видео (404
 * `no_source_video`), пара переведена не с русского (409 `pair_not_from_russian`). Его сообщение
 * написано для оператора (id, пути, архив) — здесь суть; null — показать слова сервера.
 */
const cutPausesRefusal = (error: ApiError): string | null => {
    const detail: any = error.detail;
    if (!detail || typeof detail !== "object") return null;
    switch (errorReason(error)) {
        case "cues_without_dub": {
            const cues: any[] = Array.isArray(detail.cues) ? detail.cues : [];
            const count = typeof detail.count === "number" ? detail.count : cues.length;
            if (!count) return null;
            const shown = cues.slice(0, 3).map((cue) => `${cue?.time ?? "?"} «${cue?.captions ?? ""}»`);
            const more = count > shown.length ? ` и ещё ${count - shown.length}` : "";
            const list = shown.length ? `: ${shown.join(", ")}${more}` : "";
            return `Паузы не вырезаны — нет озвучки у ${cuesRuGen(count)}${list}.`;
        }
        case "no_source_video": {
            const folder = detail.folder != null ? ` ${detail.folder}` : "";
            return `Паузы не вырезаны — у папки${folder} нет исходного видео, озвучку не на что наложить.`;
        }
        case "pair_not_from_russian": {
            const pair = detail.pair ? ` ${detail.pair}` : "";
            return `Паузы не вырезаны — пара${pair} переведена не с русского, а видео папки собирается только у пар с русского.`;
        }
        default:
            return null;
    }
};
